import React, { useCallback, useEffect, useState } from 'react';

import { Group, Stack, Text, createStyles, useMantineTheme } from '@mantine/core';
import { Eventcalendar, MbscCalendarEvent } from '@mobiscroll/react';
import dayjs from 'dayjs';
import useAppointmentMonth from 'hooks/appointment/useAppointmentMonth';
import useClassMonth from 'hooks/class/useClassMonth';
import useDebounce from 'hooks/useDebounce';
import getTotalEvents, { CalendarTileData } from 'utils/scheduling.helpers';

import { EventCalendarView } from './types';

const useStyles = createStyles(theme => ({
  calendar: {
    fontFamily: `${theme.fontFamily} !important`,
    borderRadius: theme.radius.md,
  },
}));

interface FilterTerms {
  eventTypes: string[];
  coaches: string[];
  locations: string[];
}

interface IEventCalendarMonthViewProps {
  eventCalendarNavigationButtons: () => JSX.Element;
  searchFilters: FilterTerms;
  setView: React.Dispatch<React.SetStateAction<EventCalendarView>>;
  setCreateEventDate: React.Dispatch<React.SetStateAction<Date>>;
  calendarMaxDate?: Date;
  isDesktop: boolean;
}

const EventCalendarMonthView = ({
  eventCalendarNavigationButtons,
  searchFilters,
  setView,
  setCreateEventDate,
  calendarMaxDate,
  isDesktop,
}: IEventCalendarMonthViewProps): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const [start, setStart] = useState<string>();
  const [end, setEnd] = useState<string>();
  const debouncedStart = useDebounce(start, 200);
  const debouncedEnd = useDebounce(end, 200);
  const [calendarEvents, setCalendarEvents] = useState<MbscCalendarEvent[]>([]);
  const [maxTotalEvents, setMaxTotalEvents] = useState(1);

  const {
    data: classData,
    isError: classIsError,
    isSuccess: classIsSuccess,
  } = useClassMonth({
    start: debouncedStart,
    end: debouncedEnd,
    ...searchFilters,
  });

  const {
    data: appointmentData,
    isError: appointmentIsError,
    isSuccess: appointmentIsSuccess,
  } = useAppointmentMonth({
    start: debouncedStart,
    end: debouncedEnd,
    ...searchFilters,
  });

  const onPageLoading = (event: {
    month?: string | number | Date | dayjs.Dayjs | null | undefined;
    firstDay: string | number | Date | dayjs.Dayjs | null | undefined;
    lastDay: string | number | Date | dayjs.Dayjs | null | undefined;
  }): void => {
    const month = dayjs(event.month);
    const today = dayjs();
    const isCurrentMonth = month.month() === today.month() && month.year() === today.year();
    setCreateEventDate(isCurrentMonth ? today.toDate() : month.toDate());
    const s = dayjs(event.firstDay).format('YYYY-MM-DD');
    const e = dayjs(event.lastDay).format('YYYY-MM-DD');
    setStart(s);
    setEnd(e);
  };

  const handleCellClick = useCallback(
    (data: any): void => {
      setCreateEventDate(dayjs(data.date).toDate());
      setView('day');
    },
    [setCreateEventDate, setView],
  );

  const renderLabel = useCallback(
    (data: any): JSX.Element => {
      const { totalAppointments, totalClasses, backgroundColor } = data.original;
      return (
        <Stack
          p={`calc(${theme.spacing.xs} / 4)`}
          sx={{
            gap: 0,
            backgroundColor,
            borderRadius: `calc(${theme.spacing.xs} / 5)`,
          }}
          data-cy='monthViewEventsGroup'
        >
          <Group sx={{ gap: `calc(${theme.spacing.xs} / 2)` }}>
            <Text fw='bold'>{totalAppointments}</Text>
            <Text sx={isDesktop ? {} : { fontSize: '0.35rem' }}>
              Appointment{totalAppointments > 1 && 's'}
            </Text>
          </Group>
          <Group sx={{ gap: `calc(${theme.spacing.xs} / 2)` }}>
            <Text fw='bold'>{totalClasses}</Text>
            <Text sx={isDesktop ? {} : { fontSize: '0.5rem' }}>Class{totalClasses > 1 && 'es'}</Text>
          </Group>
        </Stack>
      );
    },
    [theme, isDesktop],
  );

  useEffect(() => {
    if (appointmentIsSuccess && appointmentData && classIsSuccess && classData) {
      const totalEvents = getTotalEvents(classData.data, appointmentData.data);
      setMaxTotalEvents(totalEvents[1]);
      setCalendarEvents(
        totalEvents[0].map((event: CalendarTileData): MbscCalendarEvent => {
          return {
            ...event,
            backgroundColor: `rgb(0, 158, 227, ${
              (event.totalAppointments + event.totalClasses) / maxTotalEvents
            })`,
          };
        }),
      );
    }
  }, [theme, classIsSuccess, classData, maxTotalEvents, appointmentIsSuccess, appointmentData]);

  if (appointmentIsError || classIsError) {
    return <>Something went wrong</>;
  }
  return (
    <>
      <Eventcalendar
        renderHeader={eventCalendarNavigationButtons}
        theme='ios'
        themeVariant='light'
        className={classes.calendar}
        data={calendarEvents}
        renderLabel={renderLabel}
        onPageLoading={onPageLoading}
        onCellClick={handleCellClick}
        max={calendarMaxDate}
        view={{
          calendar: { labels: true },
        }}
        width={isDesktop ? '100%' : '100vw'}
      />
    </>
  );
};

export default EventCalendarMonthView;
